import type { TopicMatch, TopicResult } from './prepareApi';

/**
 * Turning chosen passages into notes someone can paste elsewhere.
 *
 * Separate from the screen because this is the part that has to be right: once
 * text leaves the product, the citation is the only thing standing between a
 * quotation and someone treating an unreviewed AI translation as a publisher's
 * words. Pure functions over data, so the format can be tested without a
 * browser — which matters, because the clipboard cannot be read in a test.
 */

function formatFriday(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Where links in the notes point back to. Read from the browser, once. */
export function notesOrigin(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/** Where a citation points. Absolute, because notes are read outside this tab. */
export function passageLink(
  origin: string,
  khutbahId: string,
  language: string,
  passageId: string,
): string {
  const query = `?khutbah=${encodeURIComponent(khutbahId)}&language=${encodeURIComponent(language)}`;
  return `${origin}${query}#passage-${passageId}`;
}

/**
 * The provenance of one translation, in words.
 *
 * Words rather than a badge because notes are plain text: a reader of the
 * pasted version has no colour, no icon and no popover, so the claim has to
 * survive as a sentence. The four cases are the ones the badge distinguishes,
 * and conflating "a person wrote it" with "a person checked it" is the
 * mistake this wording exists to prevent.
 */
export function provenanceLine(match: TopicMatch): string {
  if (match.translationText === null) return 'no published translation in this language';
  if (match.translationOrigin === 'publisher') return "publisher's own translation";
  if (match.translationReviewed) return 'translation reviewed by a person';
  if (match.translationOrigin === 'human') return 'translation by a person, unreviewed';
  return 'AI translation, unreviewed';
}

/**
 * Assembles the selected passages into notes.
 *
 * Plain text, so it can be pasted anywhere, and every quotation sits directly
 * above its own citation. The header is not decoration: it is the statement
 * that travels with the text once it leaves this screen, and it is the reason
 * this feature can exist at all under a rule that forbids fabricating
 * religious content — what leaves here is quotation, and it says so.
 *
 * @param today injectable so the test is not a function of the day it runs
 */
export function buildNotes(
  origin: string,
  topic: string,
  language: string,
  results: readonly TopicResult[],
  selected: ReadonlySet<string>,
  today: Date = new Date(),
): string {
  const date = today.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const lines: string[] = [
    `Notes from published khutbahs · searched for "${topic}" · ${date}`,
    '',
    'Every passage below is quoted from a published khutbah. Nothing here was',
    'written by an assistant. Check each quotation against the original before',
    'relying on it; the link after each one opens the passage in context.',
    '',
  ];

  for (const result of results) {
    const chosen = result.matches.filter((match) => selected.has(match.passageId));
    if (chosen.length === 0) continue;

    lines.push('─'.repeat(60));
    lines.push(`${result.institutionName} · Friday ${formatFriday(result.friday)}`);
    if (result.titleOriginal) lines.push(result.titleOriginal);
    lines.push(
      result.verifiedPublisher
        ? 'Verified publisher source'
        : 'Source not verified as a publisher',
    );
    lines.push('');

    for (const match of chosen) {
      lines.push(match.sourceText);
      if (match.translationText) lines.push(match.translationText);
      if (match.uncertain) {
        lines.push('[this passage lost text that could not be read reliably]');
      }
      lines.push(`— passage ${match.ordinal + 1} · ${provenanceLine(match)}`);
      lines.push(`  ${passageLink(origin, result.khutbahId, language, match.passageId)}`);
      lines.push('');
    }
  }
  return lines.join('\n');
}
